import './DTXAd.scss';
import { useTranslation } from 'react-i18next';
import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { isDntEnabled } from '@utils/dnt.ts';
import { renderAd } from '@pages/Root/utils/dtx-ads.ts';
import { useSearchParams } from 'react-router';
import { BannerAdSizes } from '@moment/components/GalleryAd/ad-constants.ts';
import { LazyAd } from '@moment/components/LazyAd/LazyAd.tsx';
import { useAds } from '@/moment/context/AdsContext.tsx';
import {
  DEFAULT_AD_HEIGHT,
  DEFAULT_BANNER_HEIGHT,
} from '@moment/shared-code/ad-utils.tsx';

export interface DTXAdProps {
  adUnitId: string;
  containerClassName?: string;
  adBlockClassName?: string;
  adSize: BannerAdSizes[];
  tagId: string;
  fallbackAd?: ReactElement | null;
  iabCategories?: string[];
}

export const DTXAd: FC<DTXAdProps> = (props) => {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [adLoaded, setAdLoaded] = useState(false);
  const [displayAd, setDisplayAd] = useState(false);
  const [isDoNotSell] = useState<boolean>(isDntEnabled());
  const [isInView, setIsInView] = useState(false);

  const { isReady } = useAds();

  const pageParams = useMemo(() => {
    return {
      dtxTestResponseId: searchParams.get('dtx-test-response-id'),
      dtxTestDistributorId: searchParams.get('dtx-test-distributor-id'),
    };
  }, [searchParams]);
  const [showFallbackAd, setShowFallbackAd] = useState(false);

  const adSlotId = `dtx_ad_unit_${props.adUnitId}`;
  const adTestData =
    pageParams.dtxTestResponseId && pageParams.dtxTestDistributorId
      ? {
          mock_response_id: pageParams.dtxTestResponseId,
          distributor_id: Number(pageParams.dtxTestDistributorId),
        }
      : null;

  useEffect(() => {
    const renderDtxAd = async () => {
      console.debug(
        `Rendering ad ${adSlotId}, iabCategories=${JSON.stringify(props.iabCategories)}, test_data=${JSON.stringify(adTestData)}`
      );
      // NOTE: iabCategories do not currently conform to DTX standard, omitting for now
      return renderAd(
        adSlotId,
        props.tagId,
        props.adSize,
        lang,
        undefined,
        adTestData
      );
    };

    if (!adLoaded && isReady && isInView) {
      if (!isDoNotSell) {
        renderDtxAd().then((success) => {
          console.debug(`Ad ${adSlotId} rendered? ${success}`);
          setDisplayAd(success);
          // success events are handled in DTXAdsLayout
          if (!success) {
            if (props.fallbackAd) {
              console.debug(`Displaying fallback ad for ${adSlotId}`);
              setShowFallbackAd(true);
            }
          }
        });
      } else {
        setDisplayAd(false);
      }
      setAdLoaded(true);
    }
  }, [
    adLoaded,
    isReady,
    isInView,
    isDoNotSell,
    adSlotId,
    props.tagId,
    props.adSize,
    lang,
    adTestData,
    props.fallbackAd,
    props.iabCategories,
  ]);

  const adSkeletonHeight = useMemo(
    // add 30 for banner height
    () => props.adSize?.[0]?.toDtxSizing()?.height || DEFAULT_AD_HEIGHT,
    [props.adSize]
  );

  return useMemo(() => {
    if (isDoNotSell) return null;
    return !showFallbackAd ? (
      <LazyAd
        id={`dtx_ad_${adSlotId}`}
        onInViewChange={setIsInView}
        height={adSkeletonHeight}
      >
        {adLoaded ? (
          <div className={props.containerClassName}>
            <div
              key="ad"
              className={props.adBlockClassName}
              id={adSlotId}
              dt-debug-dtx-pid={props.tagId}
              style={{ minHeight: DEFAULT_BANNER_HEIGHT }}
            ></div>
          </div>
        ) : null}
      </LazyAd>
    ) : (
      props.fallbackAd
    );
  }, [
    isDoNotSell,
    adLoaded,
    showFallbackAd,
    displayAd,
    adSlotId,
    props.containerClassName,
    props.adBlockClassName,
    props.tagId,
    t,
    adSkeletonHeight,
    isInView,
  ]);
};
